import type {
  ControlCenter,
  ControlSignalStatus,
} from "@/features/admin";
import { cn } from "@/lib/utils";
import {
  CheckCircleIcon,
  QuestionIcon,
  WarningCircleIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { STATUS_LABEL, STATUS_VAR } from "./format";

const ICON: Record<ControlSignalStatus, typeof CheckCircleIcon> = {
  ok: CheckCircleIcon,
  warn: WarningIcon,
  bad: WarningCircleIcon,
  unknown: QuestionIcon,
};

/**
 * The verdict, then the signals it was made from. Every signal says *why* in one line, so
 * an amber banner is a reason to read one chip, not to open six pages.
 */
export function HealthBanner({
  health,
  live,
}: {
  health: ControlCenter["health"];
  live: ControlCenter["live"];
}) {
  const Icon = ICON[health.status];
  const color = STATUS_VAR[health.status];

  return (
    <div
      className="relative overflow-hidden rounded-xl border bg-card p-5"
      style={{
        backgroundImage: `radial-gradient(120% 140% at 0% 0%, color-mix(in srgb, ${color} 16%, transparent) 0%, transparent 55%)`,
      }}
    >
      <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
        <div className="flex items-center gap-4 min-w-0">
          <div
            className="relative flex size-14 shrink-0 items-center justify-center rounded-full"
            style={{
              background: `color-mix(in srgb, ${color} 15%, transparent)`,
            }}
          >
            {health.status !== "unknown" ? (
              <span
                className="absolute inset-0 rounded-full animate-ping opacity-20"
                style={{ background: color, animationDuration: "2.4s" }}
              />
            ) : null}
            <Icon size={30} weight="fill" style={{ color }} />
          </div>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              État de la plateforme
            </div>
            <div className="text-2xl font-semibold tracking-tight">
              {STATUS_LABEL[health.status]}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <LiveCounter value={live.calls} label="appels en cours" />
          <LiveCounter value={live.chats} label="chats en cours" />
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-2">
        {health.signals.map((s) => (
          <div
            key={s.key}
            className={cn(
              "rounded-lg border bg-background/60 px-3 py-2 backdrop-blur",
            )}
            style={{
              borderColor:
                s.status === "ok" || s.status === "unknown"
                  ? undefined
                  : `color-mix(in srgb, ${STATUS_VAR[s.status]} 45%, transparent)`,
            }}
          >
            <div className="flex items-center gap-2 text-xs font-medium">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ background: STATUS_VAR[s.status] }}
              />
              {s.label}
            </div>
            <div
              className="mt-0.5 truncate text-xs text-muted-foreground"
              title={s.detail}
            >
              {s.detail}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LiveCounter({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="relative flex size-3">
        {value > 0 ? (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
        ) : null}
        <span
          className={cn(
            "relative inline-flex size-3 rounded-full",
            value > 0 ? "bg-success" : "bg-muted-foreground/40",
          )}
        />
      </span>
      <div>
        <div className="text-3xl font-bold tabular-nums leading-none">
          {value}
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}
